import { useCallback } from "react"
import { IconNotepadOutline18 as FileTextIcon } from "nucleo-ui-outline-18"
import { z } from "zod"

import { Button, Form, FormInput, Label, useZodForm } from "@productlane/ui"

import { toast } from "@/components/toast"

interface MarkdownUploadStepProps {
	onSuccess: (files: FileList) => void
	isParsing?: boolean
}

function isValidFile(file: File) {
	const name = file.name.toLowerCase()
	return name.endsWith(".md") || name.endsWith(".zip")
}

export function MarkdownUploadStep({
	onSuccess,
	isParsing = false,
}: MarkdownUploadStepProps) {
	const form = useZodForm({
		schema: z.object({
			files: z
				.instanceof(FileList)
				.refine((files) => files.length > 0, "Please select at least one file"),
		}),
	})

	const files = form.watch("files")

	const handleSubmit = useCallback(
		({ files }: { files: FileList }) => {
			const invalidFiles = Array.from(files).filter(
				(file) => !isValidFile(file),
			)

			if (invalidFiles.length > 0) {
				toast.error({
					title: "Invalid file type",
					description: "Only .md and .zip files are allowed",
				})
				return
			}

			onSuccess(files)
		},
		[onSuccess],
	)

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault()
			const droppedFiles = e.dataTransfer.files
			const validFiles = Array.from(droppedFiles).filter(isValidFile)

			if (validFiles.length === 0) {
				toast.error({
					title: "No valid files",
					description: "Only .md and .zip files are allowed",
				})
				return
			}

			if (validFiles.length !== droppedFiles.length) {
				toast.info({
					title: "Some files ignored",
					description: "Only .md and .zip files were kept",
				})
			}

			const dataTransfer = new DataTransfer()
			validFiles.forEach((file) => dataTransfer.items.add(file))
			form.setValue("files", dataTransfer.files)
		},
		[form],
	)

	return (
		<Form form={form} onSubmit={handleSubmit} className="space-y-4">
			<Label htmlFor="file-upload">
				<div
					onDrop={handleDrop}
					onDragOver={(e) => e.preventDefault()}
					className="mt-2 flex flex-col items-center rounded-lg border border-dashed border-gray-400/60 bg-white px-6 py-10 dark:border-gray-500 dark:bg-gray-900"
				>
					<FileTextIcon className="h-6 w-6 text-gray-400" />
					<div className="mt-4">
						{files && files.length > 0 ? (
							<div className="text-center">
								<div className="font-medium text-primary">
									{files.length} file{files.length > 1 ? "s" : ""} selected
								</div>
								<div className="mt-1 text-sm text-secondary">
									{Array.from(files)
										.map((file) => file.name)
										.join(", ")}
								</div>
							</div>
						) : (
							<>
								<span className="font-medium text-primary">
									Upload Markdown files or a ZIP
								</span>
								<span className="pl-1 font-normal text-secondary">
									or drag and drop
								</span>
							</>
						)}
					</div>
				</div>
				<FormInput
					id="file-upload"
					type="file"
					name="files"
					accept=".md,.zip"
					multiple
					className="sr-only"
				/>
			</Label>

			<Button
				className="float-right"
				type="submit"
				disabled={!files || files.length === 0 || isParsing}
				isLoading={isParsing}
			>
				Upload {files && files.length > 1 ? "Files" : "File"}
			</Button>
		</Form>
	)
}
